import { useEffect, useState } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import {
  CONNECT_PROVIDER_LABELS,
  publishConnectResult,
  readConnectLanding,
} from '@/utils/oauth-connect-signal';

// Long enough for a close to take effect before the page admits it is staying.
const CLOSE_GRACE_MS = 400;

/**
 * Landing page for a finished consent round-trip, and nothing else.
 *
 * Deliberately outside the authenticated shell: in the popup case this exists
 * only to report the result, so booting the app would mount a second copy of
 * whatever page the user started from inside a 520x680 window. `main.tsx` mounts
 * it directly for that reason — as a route it sat inside `LaunchDarklyWrapper`,
 * whose spinner withheld the report until auth and the current org resolved and
 * dropped it altogether if the window was closed first. Do not make it a route.
 *
 * Closing is attempted but rarely succeeds. The consent popup is opened with
 * its `opener` severed, which disqualifies it from being script-closable — the
 * price of keeping the provider's page from navigating the tab behind it. So
 * the page asks to be closed rather than pretending it will vanish.
 *
 * It offers no way back, deliberately. Severing also means it cannot tell a
 * popup from a tab, and a link followed inside the popup would load the
 * workflow editor into a 520x680 window — the failure this page exists to
 * prevent. Nothing is lost by leaving it out: the connect card never navigates
 * the tab it was clicked from, so that tab is always still there.
 */
export const OAuthConnectedPage = () => {
  const [stayed, setStayed] = useState(false);
  const landing = readConnectLanding(window.location.search);
  const label = landing ? CONNECT_PROVIDER_LABELS[landing.provider] : null;

  useEffect(() => {
    if (landing) {
      publishConnectResult({
        provider: landing.provider,
        connected: landing.connected,
      });
    }
    window.close();

    const timer = window.setTimeout(() => setStayed(true), CLOSE_GRACE_MS);
    return () => window.clearTimeout(timer);
    // Runs once: the outcome comes from the URL this document loaded with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connected = landing?.connected ?? false;

  return (
    <div className='flex min-h-screen items-center justify-center bg-background p-6'>
      <div className='flex max-w-sm flex-col items-center gap-3 text-center'>
        {connected ? (
          <CheckCircle2 className='size-8 text-green-600' />
        ) : (
          <XCircle className='size-8 text-destructive' />
        )}
        <p className='text-base font-medium'>
          {connected
            ? `${label} connected`
            : `${label ?? 'Authorization'} did not complete`}
        </p>
        {stayed && (
          <p className='text-sm text-muted-foreground'>
            You can close this window — your workflow is still open in the tab
            you started from.
          </p>
        )}
      </div>
    </div>
  );
};

export default OAuthConnectedPage;
